import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  Alert,
  ActivityIndicator,
  ScrollView,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  Linking,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import OnboardingLayout from '@/components/OnboardingLayout';
import CircularArrowButton from '@/components/CircularArrowButton';
import { useLanguage } from '@/contexts/LanguageContext';

export default function VerifyCodeScreen() {
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const router = useRouter();
  const params = useLocalSearchParams();
  
  const email = typeof params.email === 'string' ? params.email : '';
  const termsAccepted = params.termsAccepted === 'true';
  
  const inputRefs = useRef<(TextInput | null)[]>([]);

  useEffect(() => {
    if (timer === 0) return;
    
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  const handleCodeChange = (value: string, index: number) => {
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyPress = (key: string, index: number) => {
    if (key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyCode = async () => {
    const fullCode = code.join('');
    
    if (fullCode.length !== 6) {
      Alert.alert(t('auth.verifyCode.errorTitle'), t('auth.verifyCode.errorInvalidCode'));
      return;
    }

    setLoading(true);

    try {
      console.log('🔐 Verifying OTP with custom server for:', email);
      
      const verifyResponse = await fetch('https://mobile.padrinho.app/api/auth/verify-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          otp_code: fullCode
        }),
      });

      const verifyData = await verifyResponse.json();

      if (!verifyResponse.ok || !verifyData.success) {
        console.error('❌ OTP verification failed:', verifyData);
        // PRD §5.1 — `auth_verify_code_submitted` failure branch. Map the
        // server's "expired" copy to the bounded `expired` enum so the
        // dashboard can split timeout-driven dropoff from typos.
        const errStr = String(verifyData.error || '').toLowerCase();
        import('@/utils/clarityEvents')
          .then(({ logAuthVerifyCodeSubmitted }) =>
            logAuthVerifyCodeSubmitted({
              result: errStr.includes('expir') ? 'expired' : 'invalid',
            })
          )
          .catch(() => {});
        Alert.alert(t('auth.verifyCode.errorTitle'), verifyData.error || t('auth.verifyCode.errorInvalidCode'));
        return;
      }

      console.log('✅ OTP verified successfully via custom server');

      // Fire-and-forget Clarity event. No PII — only the static purpose enum.
      import('@/utils/clarityEvents')
        .then(({ logClarityEvent }) =>
          logClarityEvent('auth_otp_verified', { purpose: 'signup' })
        )
        .catch(() => {});

      // PRD §5.1 — `auth_verify_code_submitted` success branch. Pairs with
      // `auth_otp_verified` so the dashboard sees both intent volume
      // (success+invalid+expired) and outcome (success only).
      import('@/utils/clarityEvents')
        .then(({ logAuthVerifyCodeSubmitted }) =>
          logAuthVerifyCodeSubmitted({ result: 'success' })
        )
        .catch(() => {});

      const verificationToken = verifyData.verificationToken;
      
      if (!verificationToken) {
        console.error('❌ No verification token received');
        Alert.alert(t('auth.verifyCode.errorTitle'), t('auth.verifyCode.errorUnexpected'));
        return;
      }

      console.log('🎫 Verification token received, proceeding to password setup');

      router.push({
        pathname: '/set-password',
        params: { 
          email: email,
          verificationToken: verificationToken,
          termsAccepted: termsAccepted.toString()
        }
      });

    } catch (error) {
      console.error('❌ Unexpected error during OTP verification:', error);
      // PRD §5.1 — `auth_verify_code_submitted` failure branch for the
      // unexpected catch (network / JSON parse / etc).
      import('@/utils/clarityEvents')
        .then(({ logAuthVerifyCodeSubmitted }) =>
          logAuthVerifyCodeSubmitted({ result: 'invalid' })
        )
        .catch(() => {});
      Alert.alert(t('auth.verifyCode.errorTitle'), t('auth.verifyCode.errorUnexpected'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCount >= 3) {
      return;
    }

    setResendLoading(true);

    try {
      console.log('📧 Resending OTP for:', email, `(Attempt ${resendCount + 1}/3)`);
      
      const response = await fetch('https://mobile.padrinho.app/api/auth/send-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          purpose: 'signup'
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        console.error('❌ Error resending OTP:', data);
        Alert.alert(t('auth.verifyCode.errorTitle'), data.error || t('auth.verifyCode.errorResendFailed'));
        return;
      }

      console.log('✅ OTP resent successfully');

      // Fire-and-forget Clarity event. attempt is a small integer, no PII.
      import('@/utils/clarityEvents')
        .then(({ logClarityEvent }) =>
          logClarityEvent('auth_otp_requested', {
            purpose: 'signup',
            source: 'verify_code_resend',
            attempt: resendCount + 1,
          })
        )
        .catch(() => {});

      Alert.alert(t('auth.verifyCode.successTitle'), t('auth.verifyCode.successResent'));
      
      setResendCount(prev => prev + 1);
      setTimer(60);
      setCanResend(false);
      setCode(['', '', '', '', '', '']);

    } catch (error) {
      console.error('❌ Unexpected error during OTP resend:', error);
      Alert.alert(t('auth.verifyCode.errorTitle'), t('auth.verifyCode.errorUnexpected'));
    } finally {
      setResendLoading(false);
    }
  };

  const formatTimer = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const progressWidth = 72; // Step 6 of 7

  return (
    <OnboardingLayout progressWidth={progressWidth}>
      <KeyboardAvoidingView
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView 
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Step Section */}
          <View style={styles.section}>
            <View style={styles.stepBadge}>
              <Text style={styles.stepNumber}>6</Text>
            </View>

            <View style={styles.textContainer}>
              <Text style={styles.title}>{t('auth.verifyCode.title')}</Text>
              <Text style={styles.subtitle}>
                {t('auth.verifyCode.subtitle', { email })}
              </Text>
            </View>
          </View>

          {/* Code Input Section */}
          <View style={styles.codeSection}>
            <View style={styles.codeContainer}>
              {code.map((digit, index) => (
                <TextInput
                  key={index}
                  ref={(ref) => { inputRefs.current[index] = ref; }}
                  style={[
                    styles.codeInput,
                    digit ? styles.codeInputFilled : null,
                    { color: '#151515' }
                  ]}
                  value={digit}
                  onChangeText={(value) => handleCodeChange(value, index)}
                  onKeyPress={({ nativeEvent }) => handleKeyPress(nativeEvent.key, index)}
                  keyboardType="number-pad"
                  maxLength={1}
                  selectTextOnFocus
                  autoFocus={index === 0}
                />
              ))}
            </View>

            {/* Resend Section */}
            <View style={styles.resendContainer}>
              {resendCount >= 3 ? (
                <Text style={styles.contactText}>
                  {t('auth.verifyCode.contactMessage')}{' '}
                  <Text 
                    style={styles.contactEmail}
                    onPress={() => Linking.openURL(`mailto:${t('auth.verifyCode.contactEmail')}`)}
                  >
                    {t('auth.verifyCode.contactEmail')}
                  </Text>
                </Text>
              ) : canResend ? (
                <TouchableOpacity 
                  onPress={handleResendCode}
                  disabled={resendLoading}
                  style={styles.resendButton}
                >
                  {resendLoading ? (
                    <ActivityIndicator size="small" color="#015480" />
                  ) : (
                    <Text style={styles.resendText}>{t('auth.verifyCode.resendButton')}</Text>
                  )}
                </TouchableOpacity>
              ) : (
                <Text style={styles.timerText}>
                  {t('auth.verifyCode.resendIn', { timer: formatTimer(timer) })}
                </Text>
              )}
            </View>
          </View>
        </ScrollView>

        {/* Continue Button */}
        <View style={[styles.buttonContainer, { bottom: insets.bottom + 24 }]}>
          <CircularArrowButton
            onPress={handleVerifyCode}
            disabled={code.join('').length !== 6}
            loading={loading}
          />
        </View>
      </KeyboardAvoidingView>
    </OnboardingLayout>
  );
}

const styles = StyleSheet.create({
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  section: {
    padding: 24,
    paddingBottom: 0,
    alignItems: 'flex-start',
    gap: 24,
  },
  stepBadge: {
    backgroundColor: '#151515',
    borderRadius: 32,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepNumber: {
    color: '#FFFFFF',
    textAlign: 'center',
    fontFamily: 'Sentient-Regular',
    fontSize: 20,
    lineHeight: 28,
  },
  textContainer: {
    alignItems: 'flex-start',
    gap: 8,
  },
  title: {
    color: '#151515',
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8,
    width: 345,
  },
  subtitle: {
    color: '#56554D',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6,
    width: 345,
  },
  codeSection: {
    padding: 24,
    alignItems: 'flex-start',
    gap: 24,
  },
  codeContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 12,
    width: '100%',
  },
  codeInput: {
    flex: 1,
    height: 56,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    backgroundColor: '#FFFFFF',
    textAlign: 'center',
    fontSize: 24,
    fontFamily: 'Inter_600SemiBold',
    color: '#151515',
  },
  codeInputFilled: {
    borderColor: '#015480',
    backgroundColor: '#F8FEFF',
  },
  resendContainer: {
    alignItems: 'flex-start',
    width: '100%',
  },
  resendButton: {
    paddingVertical: 8,
  },
  resendText: {
    color: '#015480',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    textDecorationLine: 'underline',
  },
  timerText: {
    color: '#56554D',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
  },
  contactText: {
    color: '#56554D',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6,
  },
  contactEmail: {
    color: '#015480',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    textDecorationLine: 'underline',
  },
  buttonContainer: {
    position: 'absolute',
    right: 24,
  },
});
